import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AREA_COLORS } from '../constants';

// ─── Clasificador de estado de atención ───
// Devuelve UNO de 3 estados posibles para cada conversación
function clasificarEstado(conv) {
  // 1. Si tiene agente asignado → un humano la está atendiendo
  if (conv.agente_id) {
    return 'ATENDIENDO_HUMANO';
  }
  // 2. Sin agente + IA desactivada o derivada → espera humano
  if (!conv.ia_activa || conv.derivada_ia || conv.estado === 'escalada') {
    return 'ESPERANDO_HUMANO';
  }
  // 3. Sin agente + IA activa → la IA responde sola
  return 'ATENDIENDO_IA';
}

// ─── Config visual de cada estado ───
const ESTADO_CONFIG = {
  ESPERANDO_HUMANO: {
    emoji: '🟡',
    texto: 'ESPERANDO HUMANO',
    background: '#f59e0b22',
    color: '#f59e0b',
    border: '1px solid #f59e0b44',
    orden: 0, // aparece primero
  },
  ATENDIENDO_IA: {
    emoji: '🤖',
    texto: 'ATENDIENDO IA',
    background: '#64748b22',
    color: '#94a3b8',
    border: '1px solid #64748b44',
    orden: 1,
  },
  ATENDIENDO_HUMANO: {
    emoji: '🟢',
    texto: 'ATENDIENDO',
    background: '#22c55e22',
    color: '#22c55e',
    border: '1px solid #22c55e44',
    orden: 2, // aparece último
  },
};

// ─── Obtener nombre del agente por ID ───
function nombreAgente(agentes, agente_id) {
  if (!agentes || !agente_id) return '';
  const ag = agentes.find((a) => a.id === agente_id);
  // Devuelve solo el primer nombre para que entre en el badge
  if (ag?.nombre) {
    return ag.nombre.split(' ')[0];
  }
  return '';
}

function Inbox({
  filtro, setFiltro,
  areasHabilitadas,
  categoryCounts,
  conversacionesOrdenadas,
  todasLasConversaciones,
  conversacionActiva,
  seleccionarConversacion,
  obtenerBadgeEstado,
  etiquetaCanalIA,
  // ── Props nuevas ──
  agenteActualId,
  agentes,
}) {
  // ─── Ordenar por prioridad de estado ───
  // ESPERANDO_HUMANO (0) → ATENDIENDO_IA (1) → ATENDIENDO_HUMANO (2)
  // Dentro de cada grupo se mantiene el orden original (por fecha, viene de App.js)
  const conversacionesConEstado = useMemo(() => {
    return conversacionesOrdenadas.map((conv) => ({
      ...conv,
      _estado: clasificarEstado(conv),
    }));
  }, [conversacionesOrdenadas]);

  const conversacionesOrdenadaPorEstado = useMemo(() => {
    return [...conversacionesConEstado].sort((a, b) => {
      const ordenA = ESTADO_CONFIG[a._estado]?.orden ?? 9;
      const ordenB = ESTADO_CONFIG[b._estado]?.orden ?? 9;
      return ordenA - ordenB;
    });
  }, [conversacionesConEstado]);

  // ─── Filtrar según filtro activo ───
  const conversacionesFiltradas = useMemo(() => {
    // Detectar subfiltros de área: "ventas_ia", "ventas_derivadas", etc.
    const subMatch = filtro.match(/^(.+)_(ia|derivadas)$/);
    if (subMatch) {
      const area = subMatch[1];
      const tipo = subMatch[2];
      return conversacionesOrdenadaPorEstado.filter((c) => {
        const cat = c.categoria || c.area;
        if (cat !== area) return false;
        if (tipo === 'ia') return c._estado === 'ATENDIENDO_IA';
        if (tipo === 'derivadas') return c._estado === 'ESPERANDO_HUMANO';
        return false;
      });
    }

    switch (filtro) {
      case 'ia_activa':
        return conversacionesOrdenadaPorEstado.filter((c) => c._estado === 'ATENDIENDO_IA');
      case 'mi_cola':
        return conversacionesOrdenadaPorEstado.filter((c) => c._estado === 'ESPERANDO_HUMANO');
      case 'mias':
        return conversacionesOrdenadaPorEstado.filter((c) => c.agente_id === agenteActualId);
      default:
        // 'todas' y los filtros por área usan la lógica que ya viene de App.js
        return conversacionesOrdenadaPorEstado;
    }
  }, [filtro, conversacionesOrdenadaPorEstado, agenteActualId]);

  // ─── Contadores para los filtros rápidos ───
  const contadores = useMemo(() => {
    let ia = 0, cola = 0, mias = 0;
    for (const c of conversacionesConEstado) {
      if (c._estado === 'ATENDIENDO_IA') ia++;
      if (c._estado === 'ESPERANDO_HUMANO') cola++;
      if (c.agente_id === agenteActualId) mias++;
    }
    return { ia, cola, mias };
  }, [conversacionesConEstado, agenteActualId]);

  // ─── Estado del desplegable de área abierto ───
  const [areaAbierta, setAreaAbierta] = useState(null);

  // ─── Contadores por área (IA y Derivadas) ───
  // Usa todasLasConversaciones (sin filtro de área) para que los contadores
  // siempre reflejen el total real, sin importar qué filtro está activo
  const contadoresArea = useMemo(() => {
    const result = {};
    for (const area of areasHabilitadas) {
      let ia = 0, derivadas = 0;
      for (const c of (todasLasConversaciones || [])) {
        const cat = c.categoria || c.area;
        if (cat !== area) continue;
        const estado = clasificarEstado(c);
        if (estado === 'ATENDIENDO_IA') ia++;
        if (estado === 'ESPERANDO_HUMANO') derivadas++;
      }
      result[area] = { ia, derivadas };
    }
    return result;
  }, [todasLasConversaciones, areasHabilitadas]);

  const totalConv = conversacionesFiltradas.length;

  // ─── Scroll automático a la conversación activa ───
  // Depende de conversacionesFiltradas para que se dispare DESPUÉS de que
  // el Inbox renderice la lista (resuelve el caso "💬 Ver" desde el reporte,
  // donde el Inbox no existía en el DOM al momento de seleccionar).
  const listRef = useRef(null);
  const lastScrolledId = useRef(null);
  useEffect(() => {
    if (!conversacionActiva?.id || !listRef.current) return;
    // Solo hacer scroll si es un ID nuevo (evita re-scroll en cada polling)
    if (lastScrolledId.current === conversacionActiva.id) return;
    const el = listRef.current.querySelector(`[data-conv-id="${conversacionActiva.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      lastScrolledId.current = conversacionActiva.id;
    }
    // Si el elemento no existe todavía, no marcamos como scrolled.
    // El próximo render (cuando conversacionesFiltradas incluya la conv) lo reintentará.
  }, [conversacionActiva?.id, conversacionesFiltradas]);

  return (
    <section className="inbox">
      {/* Header */}
      <div className="inbox-header">
        <h2 className="inbox-title">Bandeja de entrada</h2>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{totalConv}</span>
      </div>

      {/* Quick filters — NUEVOS */}
      <div className="filter-row">
        <button
          className={`filter-chip ${filtro === 'todas' ? 'active' : ''}`}
          onClick={() => setFiltro('todas')}
        >
          🗂 Todas {categoryCounts.todas}
        </button>
        <button
          className={`filter-chip ${filtro === 'ia_activa' ? 'active' : ''}`}
          onClick={() => setFiltro('ia_activa')}
        >
          🤖 IA activa {contadores.ia > 0 ? contadores.ia : ''}
        </button>
        <button
          className={`filter-chip ${filtro === 'mi_cola' ? 'active' : ''}`}
          onClick={() => setFiltro('mi_cola')}
        >
          🟡 Mi cola {contadores.cola > 0 ? contadores.cola : ''}
        </button>
        <button
          className={`filter-chip ${filtro === 'mias' ? 'active' : ''}`}
          onClick={() => setFiltro('mias')}
        >
          🟢 Mías {contadores.mias > 0 ? contadores.mias : ''}
        </button>
      </div>

      {/* Area counts with colored dots — DESPLEGABLES */}
      <div className="area-counts">
        {areasHabilitadas.map((area) => {
          const areaLabel = area === 'atencion' ? 'Atención' : area.charAt(0).toUpperCase() + area.slice(1);
          const areaColor = AREA_COLORS[area] || 'var(--muted)';
          const isOpen = areaAbierta === area;
          const isAreaFilterActive = filtro === area || filtro === `${area}_ia` || filtro === `${area}_derivadas`;
          const counts = contadoresArea[area] || { ia: 0, derivadas: 0 };

          return (
            <div key={area}>
              {/* Fila principal del área */}
              <button
                className={`area-row ${isAreaFilterActive ? 'active' : ''}`}
                onClick={() => {
                  if (isOpen) {
                    // Si ya está abierto, cierra el desplegable y filtra por área completa
                    setAreaAbierta(null);
                    setFiltro(area);
                  } else {
                    // Abre el desplegable y filtra por área completa
                    setAreaAbierta(area);
                    setFiltro(area);
                  }
                }}
              >
                <span className="area-dot" style={{ background: areaColor }} />
                <span className="area-label">{areaLabel}</span>
                <span className="area-count">{categoryCounts[area] || 0}</span>
                <span style={{
                  marginLeft: 'auto',
                  fontSize: 10,
                  color: 'var(--muted)',
                  transition: 'transform 0.2s',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>▼</span>
              </button>

              {/* Sub-filtros desplegables */}
              {isOpen && (
                <div style={{ paddingLeft: 20 }}>
                  <button
                    className={`area-row ${filtro === `${area}_ia` ? 'active' : ''}`}
                    onClick={() => setFiltro(`${area}_ia`)}
                    style={{ fontSize: 12 }}
                  >
                    <span style={{ fontSize: 11 }}>🤖</span>
                    <span className="area-label">{areaLabel} IA</span>
                    <span className="area-count">{counts.ia || 0}</span>
                  </button>
                  <button
                    className={`area-row ${filtro === `${area}_derivadas` ? 'active' : ''}`}
                    onClick={() => setFiltro(`${area}_derivadas`)}
                    style={{ fontSize: 12 }}
                  >
                    <span style={{ fontSize: 11 }}>🟡</span>
                    <span className="area-label">{areaLabel} Derivadas</span>
                    <span className="area-count">{counts.derivadas || 0}</span>
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* List header */}
      <div className="conv-list-header">
        <span className="conv-list-label">
          {filtro === 'mi_cola' ? 'Esperando humano'
            : filtro === 'mias' ? 'Mis conversaciones'
            : filtro.endsWith('_ia') ? `${filtro.replace('_ia', '').charAt(0).toUpperCase() + filtro.replace('_ia', '').slice(1)} — IA`
            : filtro.endsWith('_derivadas') ? `${filtro.replace('_derivadas', '').charAt(0).toUpperCase() + filtro.replace('_derivadas', '').slice(1)} — Derivadas`
            : 'Más recientes'}
        </span>
      </div>

      {/* Conversation list */}
      <div className="conv-list" ref={listRef}>
        {totalConv === 0 ? (
          <div className="empty-conversations">
            {filtro === 'mi_cola'
              ? 'No hay conversaciones esperando'
              : filtro === 'mias'
              ? 'No tenés conversaciones asignadas'
              : 'No hay conversaciones'}
          </div>
        ) : (
          conversacionesFiltradas.map((conv) => {
            const isActive = conversacionActiva?.id === conv.id;
            const badgeEstado = obtenerBadgeEstado(conv);
            const estado = conv._estado;
            const config = ESTADO_CONFIG[estado];
            const inicial = (conv.nombre_cliente || 'C')[0];
            const areaColor = AREA_COLORS[conv.categoria || conv.area] || 'var(--accent)';
            const lastMsg = conv.ultimo_mensaje || 'Sin mensajes';
            const timeStr = conv.updated_at
              ? new Date(conv.updated_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
              : conv.created_at
              ? new Date(conv.created_at).toLocaleDateString('es-AR')
              : '-';

            // Texto del badge principal
            const nombreAg = estado === 'ATENDIENDO_HUMANO' ? nombreAgente(agentes, conv.agente_id) : '';
            const badgeTexto = estado === 'ATENDIENDO_HUMANO' && nombreAg
              ? `${config.emoji} ${nombreAg}`
              : `${config.emoji} ${config.texto}`;

            return (
              <button
                key={conv.id}
                data-conv-id={conv.id}
                className={`conv-item ${isActive ? 'active' : ''}`}
                onClick={() => seleccionarConversacion(conv)}
              >
                <div className="conv-avatar" style={{ background: areaColor }}>{inicial}</div>
                <div className="conv-body">
                  <div className="conv-top">
                    <span className="conv-name">
                      {conv.unread && (
                        <span style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#3b82f6',
                          marginRight: 6,
                          flexShrink: 0,
                          boxShadow: '0 0 4px #3b82f680',
                        }} />
                      )}
                      {conv.nombre_cliente || 'Sin nombre'}
                    </span>
                    <span className="conv-time">{timeStr}</span>
                  </div>
                  <div className="conv-msg">{lastMsg.length > 60 ? lastMsg.substring(0, 60) + '...' : lastMsg}</div>
                  <div className="conv-tags">
                    {/* Badge de categoría/área */}
                    <span className="tag" style={{
                      background: `${areaColor}22`,
                      color: areaColor,
                      border: `1px solid ${areaColor}44`,
                    }}>
                      {conv.categoria || 'sin categoría'}
                    </span>

                    {/* Badge derivada/crítica — se mantiene */}
                    {(conv.prioridad === 'critica' || conv.derivada_ia || conv.estado === 'escalada') && (
                      <span className="tag" style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444' }}>
                        {conv.derivada_ia || conv.estado === 'escalada' ? '🔔 Derivada' : 'Crítica'}
                      </span>
                    )}

                    {/* Badge hito outbound */}
                    {conv.origen === 'outbound' && conv.hito_outbound && (
                      <span className="tag" style={{ background: '#6366f122', color: '#818cf8', border: '1px solid #6366f144' }}>
                        📢 {conv.hito_outbound.replace(/_dash$/, '').replace(/_/g, ' ')}
                      </span>
                    )}

                    {/* Badge NUEVO — estado de atención (reemplaza ⚡IA / 👤Humano) */}
                    <span className="mode-badge" style={{
                      background: config.background,
                      color: config.color,
                      border: config.border,
                    }}>
                      {badgeTexto}
                    </span>

                    {/* Badge estado operativo — se mantiene */}
                    {badgeEstado && (
                      <span className="mode-badge" style={{
                        background: badgeEstado.estilo?.background || 'transparent',
                        color: badgeEstado.estilo?.color || 'var(--muted-2)',
                        border: badgeEstado.estilo?.border || '1px solid var(--border)',
                      }}>
                        {badgeEstado.texto}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}

export default React.memo(Inbox);
